// components
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import { Icon } from '@sdb/planning-ui';

// hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// styles
import {
  ConsequenceCell,
  ConsequenceHeaderRow,
  ConsequenceRow,
  ConsequencesBox,
  ErrorBox,
  HintText,
  WarningBox,
  WorkAgreementMeta
} from './RegisterAbsenceDialog.styles';

// utils
import { format } from 'date-fns';
import { formatByLocale, parseBackendDateTime, toDateString } from 'utils/date';

// types
import type { AbsenceRolloutDutyLine } from 'RestAPIs/roster/register-absence';

type ConsequencesPanelProps = {
  /** all existing duty lines; lines of unselected work agreements are shown
   *  as untouched instead of hidden */
  existingDutyLines: AbsenceRolloutDutyLine[];
  /** new duties (already limited to the selected work agreements) */
  newDutyLines: AbsenceRolloutDutyLine[];
  selectedWaIds: number[];
  workAgreementLabelById: Record<number, string>;
  hasSelection: boolean;
  isLoading: boolean;
  /** a calculation result actually arrived; hints and warnings wait for it */
  hasResult?: boolean;
  /** the chosen sickness duty hour type, shown on the duties being created */
  dutyHourTypeDescription?: string;
  /** detaching gives the underlying request back as an open duty (flag on the
   *  chosen duty hour type), so the planner knows replacement can be arranged */
  detachedGivesOpenDuty?: boolean;
  /** backend key of a refused rollout: shown translated instead of the
   *  misleading "changes nothing" warning */
  rolloutErrorKey?: string;
  /** editing an existing absence: duties outside the shrunk period are
   *  deleted outright (no open duty back) and there is no work agreement
   *  choice, so no row is ever "untouched" */
  isEdit?: boolean;
};

/**
 * How long the loading bar stays visible after a calculation finished. The
 * rollout often answers within a few dozen milliseconds; without this tail the
 * bar blinks once and is gone before it reads as "this is being recalculated".
 */
const LOADER_TAIL_MS = 700;

/** what happens to one duty line, presentation-wise */
type ConsequenceKind =
  | 'untouched'
  | 'unchanged'
  | 'detached'
  | 'modified'
  | 'new';

/** weekday and date from the same field: they can never contradict each
 *  other. Separate columns, so the weekday can dress up as a working day */
const formatWeekday = (line: AbsenceRolloutDutyLine) =>
  formatByLocale(parseBackendDateTime(line.startTime), 'EEEEEE');

const formatDate = (line: AbsenceRolloutDutyLine) =>
  formatByLocale(parseBackendDateTime(line.startTime), 'd MMM');

const formatTime = (line: AbsenceRolloutDutyLine) =>
  `${format(parseBackendDateTime(line.startTime), 'HH:mm')}–${format(
    parseBackendDateTime(line.endTime),
    'HH:mm'
  )}`;

/**
 * Live consequences of the absence for the planning, as one chronological
 * table: which existing duties are converted to sickness or detached (per
 * work agreement), which stay untouched because their work agreement is
 * unchecked, and which sickness duties are created on the fixed working days.
 * The action of each row is told by the duty column and its tooltip.
 */
const ConsequencesPanel = ({
  existingDutyLines,
  newDutyLines,
  selectedWaIds,
  workAgreementLabelById,
  hasSelection,
  isLoading,
  hasResult,
  dutyHourTypeDescription,
  detachedGivesOpenDuty,
  rolloutErrorKey,
  isEdit
}: ConsequencesPanelProps) => {
  const { t, i18n } = useTranslation('common');

  // a refused rollout explains itself; an unknown key falls back to a
  // generic "could not calculate" instead of claiming nothing changes
  const rolloutErrorText = rolloutErrorKey
    ? i18n.exists(rolloutErrorKey)
      ? t(rolloutErrorKey)
      : t('reactRegisterAbsence.rolloutFailed')
    : undefined;

  const waLabel = (line: AbsenceRolloutDutyLine) =>
    (line.waId && workAgreementLabelById[line.waId]) ||
    [line.waNr, line.costCenterFullName].filter(Boolean).join(' · ');

  const kindOf = (
    line: AbsenceRolloutDutyLine,
    isNew: boolean
  ): ConsequenceKind => {
    if (isNew) {
      return 'new';
    }
    // editing has no work agreement choice: every duty belongs to the absence
    if (!isEdit && (!line.waId || !selectedWaIds.includes(line.waId))) {
      return 'untouched';
    }
    // SKIP = the backend leaves this duty as it is (partial sickness)
    if (line.action === 'SKIP') {
      return 'unchanged';
    }
    return line.action === 'DELETE' ? 'detached' : 'modified';
  };

  const tooltipFor = (line: AbsenceRolloutDutyLine, kind: ConsequenceKind) => {
    switch (kind) {
      // the open-duty outcome is not repeated here: the check in its own
      // column already tells it
      case 'modified':
        return t('reactRegisterAbsence.actionModifiedTooltip', {
          type: dutyHourTypeDescription
        });
      case 'detached':
        // shrinking an absence deletes the duty outright
        if (isEdit) {
          return t('reactRegisterAbsence.actionDetachedEditTooltip');
        }
        // without an underlying request there is nothing to unlink: the
        // duty is removed, whatever the duty hour type promises
        return detachedGivesOpenDuty && line.requestId
          ? t('reactRegisterAbsence.actionDetachedOpenDutyTooltip')
          : t('reactRegisterAbsence.actionDetachedTooltip');
      case 'new':
        return t('reactRegisterAbsence.actionNewTooltip', {
          type: dutyHourTypeDescription
        });
      case 'unchanged':
        return t(
          isEdit
            ? 'reactRegisterAbsence.actionUnchangedEditTooltip'
            : 'reactRegisterAbsence.actionUnchangedTooltip'
        );
      case 'untouched':
        return t('reactRegisterAbsence.actionUntouchedTooltip');
    }
  };

  /** the duty column tells the conversion: `V107 → Ziekte`; the open-duty
   *  outcome has its own column. While editing the from-side is the type
   *  name already shown in the dropdown, so the row only tells where the
   *  duty goes — the full names would truncate unreadably */
  const dutyText = (line: AbsenceRolloutDutyLine, kind: ConsequenceKind) => {
    const code = line.displayDutyCode ?? line.dutyCode ?? '';
    if (kind === 'modified') {
      return isEdit
        ? `→ ${dutyHourTypeDescription ?? ''}`
        : `${code} → ${dutyHourTypeDescription ?? ''}`;
    }
    if (kind === 'new') {
      return dutyHourTypeDescription ?? '';
    }
    return code;
  };

  /** the original duty comes back as an open duty, to be refilled — but only
   *  when the duty carries a request (the backend unlinks per duty and skips
   *  duties without one). When editing, a duty shrunk out of the period is
   *  deleted outright: only a type conversion detaches there */
  const givesOpenDuty = (line: AbsenceRolloutDutyLine, kind: ConsequenceKind) =>
    !!detachedGivesOpenDuty &&
    !!line.requestId &&
    (kind === 'modified' || (kind === 'detached' && !isEdit));

  /** what the row means for the employee's schedule: a duty added (+), the
   *  employee taken off a duty (−), or — for a mere duty hour type
   *  conversion — neither */
  const changeMark = (kind: ConsequenceKind) => {
    if (kind === 'new') {
      return { label: '+', color: 'success.main' };
    }
    if (kind === 'detached') {
      return { label: '−', color: 'warning.main' };
    }
    return null;
  };

  // one chronological list: existing and new duties mixed, so the reader sees
  // the sick period as it will actually look instead of two separate blocks
  const sorted = [
    ...existingDutyLines.map((line) => ({ line, isNew: false })),
    ...newDutyLines.map((line) => ({ line, isNew: true }))
  ].sort(
    (a, b) =>
      parseBackendDateTime(a.line.startTime).getTime() -
      parseBackendDateTime(b.line.startTime).getTime()
  );

  // duties of the same day belong together: they share a background
  // (zebra per day); every row keeps its own day label, so a row read in
  // isolation still says when it happens
  let dayIndex = -1;
  let previousDay = '';
  const rows = sorted.map((row) => {
    const day = toDateString(parseBackendDateTime(row.line.startTime));
    if (day !== previousDay) {
      dayIndex += 1;
      previousDay = day;
    }
    return { ...row, shaded: dayIndex % 2 === 1 };
  });

  const selectedExisting = existingDutyLines.filter(
    (line) => !!line.waId && selectedWaIds.includes(line.waId)
  );

  // keep the rows visible while refreshing, so a changed date does not blank
  // the panel for a moment (react-query keeps the previous data)
  const isFirstLoad = isLoading && !existingDutyLines.length;
  const isEmpty =
    hasSelection && !selectedExisting.length && !newDutyLines.length;

  // starts visible (the dialog opens straight into a calculation) and keeps
  // running for a moment after each answer; a recalculation that starts within
  // that moment cancels the hide, so the bar does not stutter while typing
  const [showLoader, setShowLoader] = useState(true);
  useEffect(() => {
    if (isLoading) {
      setShowLoader(true);
      return;
    }
    const timer = setTimeout(() => setShowLoader(false), LOADER_TAIL_MS);

    return () => clearTimeout(timer);
  }, [isLoading]);

  // only judge "nothing happens" on a settled result — never on the
  // emptiness before the first calculation arrived, and never while the
  // opening animation still runs; during a recalculation the warning would
  // otherwise disappear and flash back
  const [settledEmpty, setSettledEmpty] = useState(false);
  useEffect(() => {
    if (hasResult && !showLoader) {
      setSettledEmpty(isEmpty);
    }
  }, [hasResult, showLoader, isEmpty]);

  return (
    <ConsequencesBox data-testid="consequences-panel">
      {/* the panel recalculates on every change, so the bar keeps its 4px slot
          whether or not it is visible: no rows jumping up and down */}
      <Box sx={{ height: 4, mb: 1 }}>
        {showLoader && (
          <LinearProgress
            data-testid="consequences-loader"
            sx={{ height: 4, borderRadius: 2 }}
          />
        )}
      </Box>
      {hasResult && !showLoader && !hasSelection && (
        <HintText>
          {t('reactRegisterAbsence.consequencesSelectWorkAgreement')}
        </HintText>
      )}
      {rolloutErrorText && (
        <ErrorBox sx={{ mb: 1 }}>{rolloutErrorText}</ErrorBox>
      )}
      {/* editing has no work agreement checkbox and no rollout, so the
          register-mode advice would be nonsense there: an absence without
          linked duties simply has none to change */}
      {!rolloutErrorText && settledEmpty && hasSelection && (
        <WarningBox sx={{ mb: 1, opacity: isLoading ? 0.6 : 1 }}>
          <Box sx={{ fontWeight: 600, mb: 0.5 }}>
            {t(
              isEdit
                ? 'reactRegisterAbsence.editNoDutiesWarning'
                : 'reactRegisterAbsence.consequencesNoneWarning'
            )}
          </Box>
          {t(
            isEdit
              ? 'reactRegisterAbsence.editNoDutiesTip'
              : 'reactRegisterAbsence.consequencesNoneTip'
          )}
        </WarningBox>
      )}
      {!isFirstLoad && rows.length > 0 && (
        <Box sx={{ opacity: isLoading ? 0.6 : 1 }}>
          <ConsequenceHeaderRow>
            <span>{t('reactRegisterAbsence.colWorkAgreement')}</span>
            <span>{t('reactRegisterAbsence.colDate')}</span>
            <span>{t('reactRegisterAbsence.colTime')}</span>
            <span>{t('reactRegisterAbsence.colDuty')}</span>
            {/* the +/− and open-duty columns explain themselves through
                their content and tooltips */}
            <span />
            <span />
          </ConsequenceHeaderRow>
          {rows.map(({ line, isNew, shaded }, index) => {
            const kind = kindOf(line, isNew);
            return (
              <ConsequenceRow
                key={isNew ? `new-${index}` : `existing-${line.id ?? index}`}
                data-testid="consequence-row"
                data-kind={kind}
                shaded={shaded}
                // greyed out: not part of the save (untouched) or removed by
                // shrinking the period while editing — the − mark stays
                sx={
                  kind === 'untouched' || (isEdit && kind === 'detached')
                    ? { opacity: 0.55 }
                    : undefined
                }
              >
                <WorkAgreementMeta
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {waLabel(line)}
                </WorkAgreementMeta>
                {/* fixed sub-columns: weekday left, date right-aligned, so
                    day numbers and month line up with minimal space */}
                <ConsequenceCell
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '20px 46px',
                    columnGap: 0.5
                  }}
                >
                  <span>{formatWeekday(line)}</span>
                  <Box component="span" sx={{ textAlign: 'right' }}>
                    {formatDate(line)}
                  </Box>
                </ConsequenceCell>
                <ConsequenceCell>{formatTime(line)}</ConsequenceCell>
                <Tooltip title={tooltipFor(line, kind)} arrow>
                  <ConsequenceCell>{dutyText(line, kind)}</ConsequenceCell>
                </Tooltip>
                {/* what the row means for the employee: + a duty added,
                    − taken off a duty, empty for a mere type conversion */}
                <ConsequenceCell sx={{ textAlign: 'center' }}>
                  {(() => {
                    const change = changeMark(kind);
                    return change ? (
                      <Tooltip title={tooltipFor(line, kind)} arrow>
                        <Box
                          component="span"
                          sx={{ color: change.color, fontWeight: 700 }}
                        >
                          {change.label}
                        </Box>
                      </Tooltip>
                    ) : null;
                  })()}
                </ConsequenceCell>
                {/* a small check: the original duty comes back as an open
                    duty, to be refilled */}
                <ConsequenceCell sx={{ textAlign: 'center' }}>
                  {givesOpenDuty(line, kind) && (
                    <Tooltip
                      title={t('reactRegisterAbsence.openDutyTooltip')}
                      arrow
                    >
                      <Box
                        component="span"
                        sx={{
                          color: 'success.main',
                          display: 'inline-flex',
                          fontSize: 15
                        }}
                      >
                        <Icon type="check" fontSize="inherit" />
                      </Box>
                    </Tooltip>
                  )}
                </ConsequenceCell>
              </ConsequenceRow>
            );
          })}
        </Box>
      )}
    </ConsequencesBox>
  );
};

export default ConsequencesPanel;
